import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent } from '../ui/Modal';
import { Button, Input } from '../ui';
import { apiService } from '../../services/api';
import { Shield, Loader2, FileText } from 'lucide-react';

interface AddRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  overflow: visible;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: visible;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Required = styled.span`
  color: ${props => props.theme.destructive || '#ef4444'};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.destructive || '#ef4444'};
  font-size: 14px;
  margin-top: 4px;
  padding: 12px;
  background: ${props => props.theme.destructive}10;
  border: 1px solid ${props => props.theme.destructive}20;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.success || '#10b981'};
  font-size: 14px;
  margin-top: 4px;
  padding: 12px;
  background: ${props => props.theme.success}10;
  border: 1px solid ${props => props.theme.success}20;
  border-radius: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.mutedForeground};
  }
`;

export const AddRoleModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!roleName.trim()) {
      setError('Role name is required');
      return;
    }

    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    // Basic validation - role name should be uppercase and alphanumeric
    const roleNameRegex = /^[A-Z0-9_]+$/;
    if (!roleNameRegex.test(roleName.toUpperCase())) {
      setError('Role name must contain only uppercase letters, numbers, and underscores');
      return;
    }

    setLoading(true);

    try {
      await apiService.createRole({
        name: roleName.toUpperCase().trim(),
        description: description.trim()
      });
      setSuccess(`Role "${roleName.toUpperCase()}" created successfully`);
      setRoleName('');
      setDescription('');
      setTimeout(() => {
        onSuccess();
        onClose();
        setSuccess(null);
      }, 1500);
    } catch (err: any) {
      console.error('Error creating role:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRoleName('');
      setDescription('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Role" size="sm" zIndex={2000}>
      <ModalContent>
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                <Shield size={16} />
                Role Name
                <Required>*</Required>
              </Label>
              <Input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., MANAGER, SUPERVISOR"
                disabled={loading}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FileText size={16} />
                Description
                <Required>*</Required>
              </Label>
              <DescriptionTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the role's responsibilities and permissions..."
                disabled={loading}
                required
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <ButtonGroup>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !roleName.trim() || !description.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                    Creating...
                  </>
                ) : (
                  'Create Role'
                )}
              </Button>
            </ButtonGroup>
          </form>
        </FormContainer>
      </ModalContent>
    </Modal>
  );
};

